import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { getRequestEmailThreads } from '../../../api'
import {
  REQUEST_LIST_API,
  REQUEST_LIST_COUNT_API,
} from '../../../constants/api'
import { RELATED_REQUESTS_ENTITY } from '../../../constants/entity'
import {
  SUBMENU_DEFAULT_VALUE,
  SUBMENU_PARAM_KEY,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import {
  useQueryParamSubTab,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { getSubSearchParam, isSubArchive } from '../../../utils/route'
import { useEmailAttachedThreadsModalOpener } from '../../Email'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import {
  getRequestAttachedFilesProps,
  RequestTableLayout,
  useRequestTablePage,
} from '../../Request'
import { TablePagination } from '../../TablePagination'
import {
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS,
  REQUEST_PROFILE_TAB_POSTS,
} from '../RequestProfile.constants'
import {
  useRequestProfileContext,
  useRequestProfileTabPerformingAction,
} from '../RequestProfile.hooks'

import { getRelatedRequestsFilterStateApi } from './RequestProfileTabAttachedRequests.api'
import {
  CONNECTED_REQUEST_FILTER_KEY,
  RELATED_REQUESTS_TABLE_CONFIG_API,
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_BUTTON_TEXT_KEY,
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_QA_NAME,
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_SUB_TAB_ORDER,
} from './RequestProfileTabAttachedRequests.constants'
import {
  useRelatedRequestsCounters,
  useRelatedRequestsTabTablePage,
  useRequestProfileTabAttachedRequestsActions,
} from './RequestProfileTabAttachedRequests.hooks'
import { RequestProfileTabAttachedRequestsSubTabs } from './RequestProfileTabAttachedRequestsSubTabs'
import { RequestProfileTabAttachedRequestsToolbar } from './RequestProfileTabAttachedRequestsToolbar'

import * as styles from './RequestProfileTabAttachedRequests.module.css'

import ServiceRequestIcon from '../../../icons/service-request.svg'

function RequestProfileTabAttachedRequests() {
  const { t } = useTranslation()

  const {
    handleCloseModal,
    handleEntityReload,
    handleLoadCounters: handleLoadProfileCounters,
    handleLoadRelatedRequestsCount,
    isPreview,
    registerRelatedRequestsReload,
    requestData,
    setModal,
  } = useRequestProfileContext()

  const requestId = requestData?.id

  const { activeSubTab, setActiveSubTab } = useQueryParamSubTab({
    allowedValues: REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_SUB_TAB_ORDER,
    isUrlBound: !isPreview,
    paramKey: SUBMENU_PARAM_KEY,
    readValue: getSubSearchParam,
  })

  const { counters, handleLoadCounters } = useRelatedRequestsCounters({
    requestId,
  })

  const isArchiveSubTab = isSubArchive(activeSubTab)

  const asyncTableOptions = {
    countApi: REQUEST_LIST_COUNT_API,
    extraFilters: {
      ...(isArchiveSubTab ? { archived: true } : null),
      [CONNECTED_REQUEST_FILTER_KEY]: [requestId],
    },
    filterStateApi: getRelatedRequestsFilterStateApi(requestId),
    isLocalPagination: isPreview,
    listApi: REQUEST_LIST_API,
    tableConfigApi: RELATED_REQUESTS_TABLE_CONFIG_API,
    withoutSubMenu: true,
  }

  const extraSelectionResetDeps = [activeSubTab, requestId]

  const { handleClickEmailTag } = useEmailAttachedThreadsModalOpener({
    counterTextKey: 'Modal.AttachedEmailsCountBtnKey',
    getAttachedFilesProps: getRequestAttachedFilesProps,
    headerTextKey: 'Modal.RequestEmailAttachedThreadsTitleKey',
    loadItems: getRequestEmailThreads,
    onCloseModal: handleCloseModal,
    onSetModal: setModal,
  })

  const canCreateRelatedRequest =
    requestData?.permissions?.can_create_related_request

  const canEditRelatedRequests =
    requestData?.permissions?.can_edit_related_requests

  function handleReloadAfterTableAction() {
    if (isPreview) {
      handleEntityReload()
    }

    handleLoadProfileCounters([
      REQUEST_PROFILE_TAB_ATTACHED_REQUESTS,
      REQUEST_PROFILE_TAB_POSTS,
    ])
    handleLoadRelatedRequestsCount()
  }

  const tablePage = useRequestTablePage({
    asyncTableOptions,
    extraSelectionResetDeps,
    handleClickEmailTag,
    setModal,
    sub: SUBMENU_DEFAULT_VALUE,
    withoutBrowserTabTitle: true,
    onCloseModal: handleCloseModal,
    onReloadActiveItem: handleReloadAfterTableAction,
  })

  const {
    availableFilters,
    columns,
    filterValues,
    handleChangeShouldReloadFilters,
    handleLoadInit,
    handleReload,
    handleSetFilterValues,
    handleSetPagination,
    hasFilterChanged,
    isLoading,
    isPerformingMassAction,
    isRefetching,
    items: requests,
    ordering,
    pageScrollWrapperRef,
    pagination,
    requiredColumns,
    shouldReloadFilters,
  } = tablePage

  useReloadCountersAfterRefetch({
    isRefetching,
    onReloadCounters: handleLoadCounters,
  })

  useEffect(() => {
    handleLoadInit()
  }, [])

  useRegisterReload({
    reload: handleReload,
    onRegisterReload: registerRelatedRequestsReload,
  })

  const {
    handleClickAttach,
    handleClickDeattach,
    handleClickMassEdit,
    handleClickRemoveAttach,
    isPerformingAction,
  } = useRequestProfileTabAttachedRequestsActions()

  useRequestProfileTabPerformingAction(
    isPerformingMassAction || isPerformingAction || isRefetching
  )

  const relatedRequestsTablePage = useRelatedRequestsTabTablePage({
    canEditRelatedRequests,
    isArchive: isArchiveSubTab,
    tablePage,
    onRemoveAttach: handleClickRemoveAttach,
  })

  const hasRequests = requests?.items?.length > 0

  const shouldCollapseToolbar = !hasRequests && !hasFilterChanged && !isLoading

  const emptyStateActions = getEmptyStateActions({
    actionName: REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_QA_NAME.emptyAttach,
    isLoading,
    qaName: REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_QA_NAME.emptyAttach,
    shouldShowActions: shouldCollapseToolbar && canCreateRelatedRequest,
    text: t(REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_BUTTON_TEXT_KEY.attach),
    onClick: handleClickAttach,
  })

  return columns && requiredColumns ? (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div
        className={styles.RequestProfileTabAttachedRequests}
        ref={pageScrollWrapperRef}
      >
        <RequestProfileTabAttachedRequestsSubTabs
          activeSubTab={activeSubTab}
          counters={counters}
          onSetActiveSubTab={setActiveSubTab}
        />
        {shouldCollapseToolbar ? null : (
          <RequestProfileTabAttachedRequestsToolbar
            availableFilters={availableFilters}
            canCreateRelatedRequest={canCreateRelatedRequest}
            canEditRelatedRequests={canEditRelatedRequests}
            filterValues={filterValues}
            isSkeleton={isLoading}
            ordering={ordering}
            pagination={pagination}
            requestId={requestId}
            setFilterValues={handleSetFilterValues}
            shouldReloadValues={shouldReloadFilters}
            onChangeShouldReloadValues={handleChangeShouldReloadFilters}
            onClickAttach={handleClickAttach}
            onClickDeattach={handleClickDeattach}
            onClickMassEdit={handleClickMassEdit}
            onReload={handleReload}
          />
        )}
        <RequestTableLayout
          emptyState={
            <PageEmptyState
              EntityIcon={ServiceRequestIcon}
              actions={emptyStateActions}
              text={t('RequestProfile.RelatedRequestsEmptyStateKey')}
            />
          }
          entity={RELATED_REQUESTS_ENTITY}
          hasTable={hasRequests || isLoading}
          tablePage={relatedRequestsTablePage}
        />
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      </div>
    </ScreenContainerRef>
  ) : null
}

export default RequestProfileTabAttachedRequests
